<script setup>
import { ref, onMounted, watch } from 'vue';
import apiFetch from '@wordpress/api-fetch';
import { useRouter } from 'vue-router';
import { getUserEmail, setContactVerified } from './utils/auth';
import { matomoTrackEvent } from './utils/matomo';

const router = useRouter();

const strings = ref(null);
const code = ref('');
const isLoading = ref(false);
const errorMessage = ref('');

const userEmail = getUserEmail();

onMounted(async () => {
  if (!userEmail) {
    router.push('/');
    return;
  }

  const lang = document.documentElement.lang?.substring(0, 2);

  try {
    const translations = await apiFetch({
      path: `/wp-json/ebus/translations?lang=${lang}`,
    });

    strings.value = translations;
  } catch (error) {
    console.error('Error: ', error);
  }
});

watch(isLoading, (value) => {
  document.body.style.overflow = value ? 'hidden' : '';
}, { immediate: true });

async function handleSubmit() {
  if (!code.value || isLoading.value || !userEmail) {
    return;
  }

  matomoTrackEvent('contact-tool', 'ct_submit_verification_code_registration');

  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await apiFetch({
      path: '/wp-json/ebus/verify-code',
      method: 'POST',
      data: {
        email: userEmail,
        code: code.value,
      },
    });

    if (response?.valid) {
      setContactVerified(true);
      router.push('/contacts-list');
    } else {
      errorMessage.value = strings.value.invalid_code;
    }
  } catch (error) {
    console.error('Error: ', error);
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <section v-if="strings" class="relative! w-full! min-h-screen! flex! flex-col! bg-light-aqua! px-6! py-9!">
    <div v-if="isLoading" class="fixed! inset-0! bg-gray/90! flex! items-center! justify-center! z-50!">
      <span class="w-20! h-20! border-4! border-aqua! border-t-transparent! rounded-full! animate-spin!"></span>
    </div>

    <div class="flex! flex-col! gap-y-3! max-w-110!">
      <h2 class="font-ibm-plex-mono! font-semibold! text-4xl! leading-tight! text-aqua!">
        <RouterLink class="text-aqua!" to="/">{{ strings.ebus_contacts_database }}</RouterLink>
      </h2>

      <div class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">
        {{ strings.verification_code_sent }}
      </div>

      <form class="w-full! max-w-75! flex! flex-col! gap-y-6!" @submit.prevent="handleSubmit">
        <input
          class="w-full! font-ibm-plex-mono! font-normal! text-base! text-black placeholder:text-black! border-b-2! border-black! pb-2! disabled:opacity-60!"
          v-model="code"
          id="code"
          type="text"
          :placeholder="strings.code"
          inputmode="numeric"
          pattern="[0-9]*"
          :disabled="isLoading"
          required
        />

        <div v-if="errorMessage" class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">
          {{ errorMessage }}
        </div>

        <button
          class="self-start! font-ibm-plex-mono! font-medium! text-base! leading-tight! text-black! py-1! px-3.75! border! border-black! transition-all rounded-full! hover:text-white! hover:bg-aqua! disabled:opacity-60! disabled:cursor-not-allowed!"
          type="submit"
          :disabled="isLoading"
        >
          {{ strings.submit }}
        </button>
      </form>
    </div>
  </section>
</template>
